<template>
  <!--    view-only mode      -->
  <template
    v-if="timesheetFormStore.isViewOnly && !timesheetFormStore.isDateDisabled(col.dateString)"
  >
    <div
      v-if="timesheetFormStore.tableDateEntries[col.dateString].lines[row.entriesKey]?.duration"
      class="tw-text-right tw-p-sm"
    >
      {{ timesheetFormStore.tableDateEntries[col.dateString].lines[row.entriesKey].duration }}
      {{
        timesheetFormStore.useDayInputs ? $t('d (day abbreviation)') : $t('h (hour abbreviation)')
      }}
    </div>
  </template>
  <template v-else>
    <!--      Input for timesheet lines      -->
    <WdsInput
      v-if="
        !timesheetFormStore.isViewOnly &&
        timesheetFormStore.tableDateEntries?.[col.dateString]?.lines?.[row.entriesKey]
      "
      v-model="timesheetFormStore.tableDateEntries[col.dateString].lines[row.entriesKey].duration"
      :data-test="`${col.dateString.split(' ').join('')}-${row.entriesKey.split(' ').join('')}`"
      class="tw-w-full"
      type="number"
      step="0.25"
      :min="0"
      :max="maxDuration"
      :disabled="timesheetFormStore.isDateDisabled(col.dateString)"
      :class="{
        'timesheet-day-total':
          !timesheetFormStore.isDateDisabled(col.dateString) &&
          !getErrorMessage(col.dateString, row.entriesKey) &&
          row.entriesKey === 'duration' &&
          timesheetFormStore.tableDateEntries[col.dateString][row.entriesKey],
      }"
      :error="getErrorMessage(col.dateString, row.entriesKey)"
      @focus="focusLineInput(col.dateString, row.entriesKey)"
      @blur="blurLineInput(col.dateString, row.entriesKey)"
      @update:model-value="handleInput"
    />
  </template>
</template>

<script setup lang="ts">
import { WdsInput } from '@worksome/wds-components'
import { computed, nextTick } from 'vue'
import { useTranslation } from '@/composables/translation'
import { useTimesheetFormStore } from '@/components/Timesheet/store/useTimesheetFormStore'
import { useValidationStore } from '@/components/Timesheet/store/useValidationStore'
import { useTimesheetLinesStore } from '@/components/Timesheet/store/useTimesheetLinesStore'
import { Message } from '@/utils/notification'
import formatFixedDecimal from '@/components/Timesheet/utilities/formatFixedDecimal'

interface Props {
  row: any
  col: any
}

const props = defineProps<Props>()

const { $t } = useTranslation()
const timesheetFormStore = useTimesheetFormStore()
const timesheetValidationStore = useValidationStore()
const timesheetLinesStore = useTimesheetLinesStore()
const resetLineDuration = (dateKey: string, fieldKey: string, value: number | null) => {
  if (
    timesheetFormStore.tableDateEntries[dateKey] !== undefined &&
    !timesheetFormStore.tableDateEntries?.[dateKey]?.lines?.[fieldKey]?.duration
  ) {
    timesheetFormStore.tableDateEntries[dateKey].lines[fieldKey].duration = value
  }
}
const focusLineInput = (dateKey: string, fieldKey: string) => {
  resetLineDuration(dateKey, fieldKey, null)
}
const blurLineInput = (dateKey: string, fieldKey: string) => {
  resetLineDuration(dateKey, fieldKey, 0)
}

const getErrorMessage = (columnDate, entryKey) =>
  timesheetValidationStore.v$[columnDate]?.[entryKey]?.$errors[0]?.$message

const maxDuration = computed(
  () =>
    Math.max(
      0,
      timesheetFormStore.sumOfTimePerDate[props.col.dateString] -
        timesheetLinesStore.sumOfTimesheetLines[props.col.dateString],
    ) +
    (timesheetFormStore.tableDateEntries?.[props.col.dateString]?.lines[props.row.entriesKey]
      ?.duration || 0),
)
const handleInput = async (e) => {
  const totalOfOtherLines =
    timesheetLinesStore.sumOfTimesheetLines[props.col.dateString] - Number(e)
  const totalAvailableTimeToAssign = Math.max(
    0,
    timesheetFormStore.sumOfTimePerDate[props.col.dateString] - totalOfOtherLines,
  )

  if (e > totalAvailableTimeToAssign) {
    await nextTick()
    new Message(
      $t('Timesheet lines cannot add up to more than the daily total time worked.'),
    ).dispatch()
    timesheetFormStore.tableDateEntries[props.col.dateString].lines[props.row.entriesKey].duration =
      formatFixedDecimal(totalAvailableTimeToAssign)
  }
}
</script>
